import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import {baseUrl} from "../Univ";
const VaccinationAdministration = ({appointment, onSaved}) => {
    const [vaccines, setVaccines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        vaccine: "",
        dose_number: 1,
        administration_date: new Date().toISOString().split("T")[0],
        administration_time: new Date().toTimeString().slice(0, 5),
        route: "",
        site: "",
        batch: "",
        dose: "",
        remarks: "",
    });
    useEffect(() => {
        loadVaccines();
    }, []);
    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    const loadVaccines = async () => {
        try {
            const response = await axios.get(`${baseUrl}vaccination/vaccines/`, {
                headers: authHeaders(),
            });
            setVaccines(response.data?.results || response.data || []);
        } catch (err) {
            console.error("Vaccine loading error:", err);
            setError("Unable to load vaccines.");
        }
    };
    const handleChange = (e) => {
        setForm({
            ...form, [e.target.name]: e.target.value,
        });
    };
    const saveAdministration = async () => {
        setLoading(true);
        setMessage("");
        setError("");
        try {
            const patientId = appointment?.patient_id || appointment?.patient?.id;
            if (!patientId) {
                setLoading(false);
                Swal.fire("Error", "Patient information is missing.", "error");
                return;
            }
            if (!form.vaccine) {
                setLoading(false);
                Swal.fire("Error", "Please select a vaccine.", "error");
                return;
            }
            const payload = {
                patient: patientId,
                appointment: appointment?.id || appointment?.appointment_id || null,
                schedule: appointment?.schedule_id || appointment?.schedule?.id || null,
                schedule_dose: appointment?.schedule_dose_id || appointment?.schedule_dose?.id || null,
                vaccine: form.vaccine,
                dose_number: Number(form.dose_number),
                administered_date: form.administration_date,
                administered_time: form.administration_time,
                vaccination_site: form.site,
                route: form.route,
                dose: form.dose,
                batch_number: form.batch,
                remarks: form.remarks,
            };
            console.log("Vaccination Administration Payload:", payload);
            const response = await axios.post(`${baseUrl}vaccination/administrations/`, payload, {
                headers: {
                    ...authHeaders(), "Content-Type": "application/json",
                },
            });
            await Swal.fire({
                icon: "success",
                title: "Success",
                text: response.data?.message || "Vaccination administration saved successfully.",
                confirmButtonText: "OK",
            });
            if (onSaved) {
                onSaved(response.data);
            }
            window.location.reload();
        } catch (err) {
            console.error("Administration save error:", err);
            const backendError = err.response?.data;
            let errorMessage = "Unable to save vaccination administration.";
            if (backendError && typeof backendError === "object") {
                errorMessage = Object.entries(backendError)
                    .map(([field, value]) => {
                        const text = Array.isArray(value) ? value.join(", ") : String(value);
                        return `${field}: ${text}`;
                    })
                    .join("\n");
            }
            Swal.fire({
                icon: "error", title: "Save Failed", text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };
    return (<Card className="vaccination-card">
        <CardContent>

            <Typography variant="h6" gutterBottom>
                Vaccination Administration
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{mb: 2}}
            >
                Record vaccine administration for the selected patient.
            </Typography>

            {message && (<Alert
                severity="success"
                sx={{mb: 2}}
            >
                {message}
            </Alert>)}

            {error && (<Alert
                severity="error"
                sx={{mb: 2}}
            >
                {error}
            </Alert>)}

            <Divider sx={{mb: 3}}/>

            <Grid container spacing={2}>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        select
                        label="Vaccine"
                        name="vaccine"
                        value={form.vaccine}
                        onChange={handleChange}
                    >
                        {vaccines.map((vaccine) => (<MenuItem
                            key={vaccine.id}
                            value={vaccine.id}
                        >
                            {vaccine.name || vaccine.vaccine_name || vaccine.my_title}
                        </MenuItem>))}
                    </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Dose Number"
                        name="dose_number"
                        value={form.dose_number}
                        onChange={handleChange}
                        inputProps={{min: 1}}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Administration Date"
                        name="administration_date"
                        value={form.administration_date}
                        onChange={handleChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="time"
                        label="Administration Time"
                        name="administration_time"
                        value={form.administration_time}
                        onChange={handleChange}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        select
                        label="Route"
                        name="route"
                        value={form.route}
                        onChange={handleChange}
                    >
                        <MenuItem value="IM">
                            Intramuscular (IM)
                        </MenuItem>

                        <MenuItem value="SC">
                            Subcutaneous (SC)
                        </MenuItem>

                        <MenuItem value="ID">
                            Intradermal (ID)
                        </MenuItem>

                        <MenuItem value="ORAL">
                            Oral
                        </MenuItem>

                        <MenuItem value="OTHER">
                            Other
                        </MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Administration Site"
                        name="site"
                        value={form.site}
                        onChange={handleChange}
                        placeholder="e.g. Left deltoid"
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Dose"
                        name="dose"
                        value={form.dose}
                        onChange={handleChange}
                        placeholder="e.g. 0.5 ml"
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Batch / Lot Number"
                        name="batch"
                        value={form.batch}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Remarks"
                        name="remarks"
                        value={form.remarks}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex", justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={saveAdministration}
                            disabled={loading}
                        >
                            {loading ? (<>
                                <CircularProgress
                                    size={20}
                                    sx={{mr: 1}}
                                />
                                Saving...
                            </>) : ("Save Administration")}
                        </Button>
                    </Box>
                </Grid>

            </Grid>

        </CardContent>
    </Card>);
};
export default VaccinationAdministration;
